import { useEffect, useRef } from "react";

/**
 * Custom hook to prevent users from accidentally leaving the page when they have unsaved changes.
 * Shows a browser confirmation dialog when trying to refresh, close tab, or navigate away.
 *
 * @param hasUnsavedChanges - Boolean indicating if there are unsaved changes
 * @param message - Optional custom message (note: most modern browsers ignore custom messages for security)
 */
export function useBeforeUnload(
  hasUnsavedChanges: boolean,
  message: string = "You have unsaved changes. Are you sure you want to leave?",
) {
  const messageRef = useRef(message);

  // Update message ref when message changes
  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Modern browsers ignore the custom message for security reasons
        // but still show a generic confirmation dialog
        event.preventDefault();
        event.returnValue = messageRef.current;
        return messageRef.current;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
}

/**
 * Alternative hook that provides more control over the confirmation behavior.
 * Useful when you want to perform additional logic before potentially leaving.
 *
 * @param shouldPrevent - Function that returns boolean indicating if navigation should be prevented
 * @param onBeforeUnload - Optional callback that runs when beforeunload is triggered
 */
export function useBeforeUnloadWithCallback(
  shouldPrevent: () => boolean,
  onBeforeUnload?: () => void,
) {
  const shouldPreventRef = useRef(shouldPrevent);
  const onBeforeUnloadRef = useRef(onBeforeUnload);

  // Update refs when functions change
  useEffect(() => {
    shouldPreventRef.current = shouldPrevent;
    onBeforeUnloadRef.current = onBeforeUnload;
  }, [shouldPrevent, onBeforeUnload]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldPreventRef.current()) {
        onBeforeUnloadRef.current?.();
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
}
